import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
  Link, // Added Link if you plan to use it for forgot password/signup
} from '@mui/material';
import { styled, useTheme } from '@mui/system';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';

import { baseUrl } from '../utils/config';

import loginImage from '../assets/images/login.png';
import sfLogo from '../assets/images/sflogo4.png';

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  maxWidth: 450,
  width: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  // Added responsive padding for small screens to prevent content touching edges
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3), // Slightly less padding on very small screens
  },
}));

const RightImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  padding: theme.spacing(3),
  // No need for explicit height on md down here if we hide it completely
  // [theme.breakpoints.down('md')]: {
  //   height: '250px',
  //   marginTop: theme.spacing(4),
  // },
}));

const LogoImage = styled('img')(({ theme }) => ({
  maxWidth: 450,
  height: 'auto',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    maxWidth: 150,
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: { // Even smaller on extra small devices
    maxWidth: 120,
    marginBottom: theme.spacing(1.5),
  },
}));

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [validationErrors, setValidationErrors] = useState({});

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      _showSnackbar('Please enter your email and password.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json();

      if (response.ok) {
        _showSnackbar(responseData.message || 'Login successful!', 'success');
        const userToStore = {
          ...responseData.user,
          token: responseData.token
        };
        localStorage.setItem('user', JSON.stringify(userToStore));
        localStorage.setItem('authToken', responseData.token);
        navigate('/dashboard', { replace: true });
      } else {
        _showSnackbar(responseData.message || 'Login failed. Please try again.', 'error');
        console.error('Login error:', responseData);
      }
    } catch (error) {
      _showSnackbar('Failed to connect to the server. Please check your network.', 'error');
      console.error('Network or server error during login:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const _showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        background: 'linear-gradient(to right, #4A148C, #1A237E, #0D47A1)',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0, md: theme.spacing(0) }, // Ensure no extra gap on mobile
        p: { xs: 2, sm: 3, md: 0 }, // Added responsive padding for the main container
        width: '100vw',
        boxSizing: 'border-box', // Crucial to prevent horizontal scroll issues with padding
      }}
    >
      {/* Left Column: Form and Welcome Back! */}
      <Box
        sx={{
          flex: { xs: 'none', md: 4 },
          minWidth: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 0, md: 0 }, // Removed inner padding here, relying on outer Box padding
          maxWidth: { md: '50%' },
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo Image */}
        <LogoImage src={sfLogo} alt="Smart Forms Logo" />

        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            mb: 4,
            color: 'white',
            fontWeight: 'bold',
            fontFamily: 'Roboto, sans-serif',
            textAlign: { xs: 'center', md: 'left' }, // Centered on mobile, left on larger screens
            width: '100%',
            maxWidth: 450,
            px: { xs: 2, sm: 0 }, // Add horizontal padding for text on small screens if maxWidth is used
          }}
        >
          Welcome Back!
        </Typography>

        <FormContainer
          elevation={6}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            maxWidth: 450,
            // Ensure FormContainer itself has some horizontal padding on very small screens
            margin: '0 auto', // Center the form container itself
          }}
        >
          <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'text.primary' }}>
            Login to Your Account
          </Typography>
          <TextField
            name="email"
            label="Email Address"
            variant="outlined"
            fullWidth
            size="medium"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            sx={{ mb: 2 }}
          />

          <TextField
            name="password"
            label="Password"
            variant="outlined"
            fullWidth
            size="medium"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{
              padding: '14px 32px',
              borderRadius: '10px',
              boxShadow: '0 5px 15px rgba(26, 35, 126, 0.4)',
              background: 'linear-gradient(to right, #4A148C, #1A237E)',
              color: 'white',
              fontSize: '18px',
              textTransform: 'none',
              mt: 2,
              '&:hover': {
                background: 'linear-gradient(to right, #1A237E, #4A148C)',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
          </Button>
          {/* Optional: Add Forgot Password / Sign Up link */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link href="#" variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Forgot password?
            </Link>
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Don't have an account?{' '}
              <Link href="#" onClick={() => navigate('/signup')} sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                Sign Up
              </Link>
            </Typography>
          </Box>
        </FormContainer>
      </Box>

      {/* Right Column: Image - Only visible on screens larger than 'md' breakpoint */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' }, // Hide on 'xs' (mobile) and 'sm' screens
          flex: 2,
          alignItems: 'center',
          justifyContent: 'center',
          ml: 0,
        }}
      >
        <Box
          component="img"
          src={loginImage}
          alt="Login Illustration"
          sx={{
            maxHeight: '80vh',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;